import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config";

const DUE_DATES = `${config.POSTLOGIN_API_URL_COMPANY}due-dates`;

/**
 * Reads which of the given KPIs are currently locked by a passed due date.
 *
 * The server evaluates this with the same service that refuses a late save, so
 * what the screen shows and what the API allows cannot drift apart. Sent as a
 * POST so the KPI id list — over a thousand for a large company — rides in the
 * body; as a GET query string it overran the proxy's URL limit and failed.
 *
 * The last two args silence apiCall's icon and success/error popup: this is a
 * background read that loads with the screen, so a POST must not raise the
 * "success" tick a mutation would — the caller renders the result instead.
 * @param {object} params the financial year, KPIs, locations and questionnaire.
 * @returns {Promise<object>} apiCall result whose data is the lock-status array.
 */
export const fetchKpiLockState = ({
  financialYearId,
  questionIds,
  locationIds = [],
  questionnaireType = "QUANT",
}) =>
  apiCall(
    `${DUE_DATES}/lock-status`,
    {},
    {
      financialYearId,
      questionIds,
      locationIds,
      questionnaireType,
    },
    "POST",
    false,
    false,
  );

/**
 * Raises a request to reopen locked KPIs for data entry.
 *
 * One call covers every locked KPI and location, rather than looping per row.
 * @param {object} payload the financial year, KPIs, locations and reason.
 * @returns {Promise<object>} apiCall result with `created` and `alreadyPending`.
 */
export const requestKpiAccess = (payload) =>
  apiCall(`${DUE_DATES}/request-access`, {}, payload, "POST");

/**
 * Grants an extension on the given access requests.
 *
 * Reuses the endpoint the approvals screen already calls, which applies the
 * extension from today and flips the request to approved.
 * @param {number[]} ids the requests to approve.
 * @param {number} increasedDays how many days to reopen entry for.
 * @returns {Promise<object>} apiCall result.
 */
export const approveKpiAccess = (ids, increasedDays, locationIds = []) =>
  apiCall(
    `${DUE_DATES}/access-requests/approve`,
    {},
    { ids, increasedDays, locationIds },
    "POST",
  );

/**
 * Declines the given access requests, leaving the KPI locked.
 * @param {number[]} ids the requests to reject.
 * @returns {Promise<object>} apiCall result.
 */
export const rejectKpiAccess = (ids, locationIds = []) =>
  apiCall(`${DUE_DATES}/access-requests/reject`, {}, { ids, locationIds }, "POST");

/**
 * Lists the access requests raised for a financial year.
 *
 * Used to show a KPI as already requested rather than offering the button
 * again.
 * @param {number} financialYearId the financial year.
 * @returns {Promise<object>} apiCall result whose data is the request array.
 */
export const fetchKpiAccessRequests = (financialYearId) =>
  apiCall(`${DUE_DATES}/access-requests`, {}, { financialYearId }, "GET");
